import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Badge, BadgeIcon } from '#/shared/ui/badge'
import { Button } from '#/shared/ui/button'
import { EditorEmpty } from '#/shared/ui/editor-empty'
import { ArrowDownIcon, ArrowUpIcon, PlusIcon } from '#/shared/ui/icons'

type PaymentVariant = 'default' | 'success' | 'danger' | 'warning'

interface PaymentBreakdown {
  amount: string
  surcharge: string
  discount: string
  totalLabel: string
  total: string
  chargedLabel: string
  charged: string
  chargedVariant?: PaymentVariant
}

interface FlowBadgeData {
  label: string
  variant?: PaymentVariant
}

interface PaymentStatusData {
  label: string
  variant?: PaymentVariant
}

interface PaymentRow {
  id: string
  direction: 'down' | 'up'
  directionVariant?: PaymentVariant
  title: string
  amount: string
  flow: FlowBadgeData
  status?: PaymentStatusData
  meta?: string
  breakdown: PaymentBreakdown
  actions?: React.ReactNode
}

interface PaymentTotalsData {
  total: string
  collected?: string
  refunded?: string
  paidToCarrier?: string
}

interface PaymentEditorProps {
  description?: string
  payments?: PaymentRow[]
  totals?: PaymentTotalsData
  onCreate?: () => void
  onCreateNew?: () => void
  onCancel?: () => void
  className?: string
  compact?: boolean
}

const AMOUNT_COLOR: Record<PaymentVariant, string> = {
  default: 'text-gray-900',
  success: 'text-green-500',
  danger: 'text-red-500',
  warning: 'text-orange-500',
}

const CHARGED_AMOUNT_COLOR: Record<PaymentVariant, string> = {
  ...AMOUNT_COLOR,
  success: 'text-green-650',
}

const STATUS_DOT_COLOR: Record<PaymentVariant, string> = {
  default: 'bg-gray-300',
  success: 'bg-green-500',
  danger: 'bg-red-500',
  warning: 'bg-yellow-500',
}

function DirectionIcon({
  direction,
  variant = 'default',
}: {
  direction: 'down' | 'up'
  variant?: PaymentVariant
}) {
  const Icon = direction === 'down' ? ArrowDownIcon : ArrowUpIcon
  return (
    <BadgeIcon variant={variant} className="size-7 rounded-full">
      <Icon className="h-1.5 w-2.5" />
    </BadgeIcon>
  )
}

function FlowBadge({ label, variant = 'default' }: FlowBadgeData) {
  return (
    <Badge
      variant={variant}
      className="h-auto rounded px-1.75 py-0.5 text-2xs font-normal"
    >
      {label}
    </Badge>
  )
}

function StatusDot({ variant = 'default' }: { variant?: PaymentVariant }) {
  return (
    <span
      aria-hidden
      className={cn(
        'size-1.5 shrink-0 rounded-full',
        STATUS_DOT_COLOR[variant],
      )}
    />
  )
}

function MetaLine({
  status,
  meta,
}: {
  status?: PaymentStatusData
  meta?: string
}) {
  if (!status && !meta) return null
  return (
    <div className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
      {status && (
        <span className="flex min-w-0 items-center gap-2">
          <StatusDot variant={status.variant} />
          <span className="truncate">{status.label}</span>
        </span>
      )}
      {status && meta && (
        <span aria-hidden className="text-gray-300">
          ·
        </span>
      )}
      {meta && <span className="truncate">{meta}</span>}
    </div>
  )
}

function Cell({
  label,
  value,
  variant = 'default',
  colorMap = AMOUNT_COLOR,
}: {
  label: string
  value: string
  variant?: PaymentVariant
  colorMap?: Record<PaymentVariant, string>
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
      <span className="text-2xs tracking-wide text-gray-300 uppercase">
        {label}
      </span>
      <span className={cn('truncate text-sm-minus', colorMap[variant])}>
        {value}
      </span>
    </div>
  )
}

function BreakdownGrid({ breakdown }: { breakdown: PaymentBreakdown }) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full items-start gap-4 px-3.5 py-2.5">
      <Cell label={t('editor.payment.cols.amount')} value={breakdown.amount} />
      <Cell
        label={t('editor.payment.cols.surcharge')}
        value={breakdown.surcharge}
      />
      <Cell
        label={t('editor.payment.cols.discount')}
        value={breakdown.discount}
      />
      <Cell label={breakdown.totalLabel} value={breakdown.total} />
      <Cell
        label={breakdown.chargedLabel}
        value={breakdown.charged}
        variant={breakdown.chargedVariant}
        colorMap={CHARGED_AMOUNT_COLOR}
      />
    </div>
  )
}

function RowHeader({ row }: { row: PaymentRow }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <DirectionIcon direction={row.direction} variant={row.directionVariant} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-2 overflow-hidden">
          <span className="shrink-0 text-sm font-medium text-gray-900">
            {row.title}
          </span>
          <span className="shrink-0 text-sm font-medium text-gray-900">
            · {row.amount}
          </span>
          <FlowBadge label={row.flow.label} variant={row.flow.variant} />
        </div>
        <MetaLine status={row.status} meta={row.meta} />
      </div>
    </div>
  )
}

function RowActions({ row }: { row: PaymentRow }) {
  if (!row.actions) return null
  return <div className="flex shrink-0 items-center gap-2.5">{row.actions}</div>
}

function PaymentRowCard({ row }: { row: PaymentRow }) {
  return (
    <div className="flex w-full shrink-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex w-full items-center gap-3 px-3.5 py-3">
        <RowHeader row={row} />
        <RowActions row={row} />
      </div>
      <span aria-hidden className="block h-px w-full bg-gray-200" />
      <BreakdownGrid breakdown={row.breakdown} />
    </div>
  )
}

function TotalCell({
  label,
  value,
  variant = 'default',
}: {
  label: string
  value: string
  variant?: PaymentVariant
}) {
  return (
    <div className="flex shrink-0 flex-col gap-0.5">
      <span className="text-2xs font-normal tracking-wide text-gray-300 uppercase">
        {label}
      </span>
      <span className={cn('text-sm font-medium', AMOUNT_COLOR[variant])}>
        {value}
      </span>
    </div>
  )
}

function TotalsRow({
  totals,
  className,
}: {
  totals: PaymentTotalsData
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'w-full shrink-0 border-y border-gray-200 px-6 py-2.5',
        className,
      )}
    >
      <div className="flex items-start justify-between">
        <TotalCell
          label={t('editor.payment.totals.total')}
          value={totals.total}
        />
        {totals.collected !== undefined && (
          <TotalCell
            label={t('editor.payment.totals.collected')}
            value={totals.collected}
            variant="success"
          />
        )}
        {totals.refunded !== undefined && (
          <TotalCell
            label={t('editor.payment.totals.refunded')}
            value={totals.refunded}
            variant="danger"
          />
        )}
        {totals.paidToCarrier !== undefined && (
          <TotalCell
            label={t('editor.payment.totals.paidToCarrier')}
            value={totals.paidToCarrier}
            variant="warning"
          />
        )}
      </div>
    </div>
  )
}

function PaymentEmpty({
  description,
  onCreate,
}: {
  description: string
  onCreate?: () => void
}) {
  const { t } = useTranslation()
  return (
    <EditorEmpty
      description={description}
      actionLabel={t('editor.payment.create')}
      onAction={onCreate}
    />
  )
}

function PaymentList({
  payments,
  totals,
  onCreateNew,
  onCancel,
}: {
  payments: PaymentRow[]
  totals?: PaymentTotalsData
  onCreateNew?: () => void
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-5 overflow-y-auto bg-gray-50 px-2.5 py-5">
        {payments.map((p) => (
          <PaymentRowCard key={p.id} row={p} />
        ))}
      </div>
      {totals && <TotalsRow totals={totals} />}
      <div className="flex h-14 shrink-0 items-center justify-end gap-2.5 px-2.5">
        {onCancel && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            className="px-2.5"
            onClick={onCancel}
          >
            {t('editor.payment.cancel')}
          </Button>
        )}
        <Button
          size="xs"
          pressEffect
          className="gap-2.5 px-3.75"
          onClick={onCreateNew}
        >
          <PlusIcon className="size-2.5" />
          {t('editor.payment.createNew')}
        </Button>
      </div>
    </div>
  )
}

function CompactPaymentRowCard({ row }: { row: PaymentRow }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-3">
      <DirectionIcon direction={row.direction} variant={row.directionVariant} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <span className="shrink-0 text-sm font-medium text-gray-900">
            {row.title}
          </span>
          <span className="shrink-0 text-sm font-medium text-gray-900">
            · {row.amount}
          </span>
          <FlowBadge label={row.flow.label} variant={row.flow.variant} />
        </div>
        <MetaLine status={row.status} meta={row.meta} />
      </div>
      <RowActions row={row} />
    </div>
  )
}

function CompactPaymentList({
  payments,
  totals,
  onCreateNew,
  onCancel,
}: {
  payments: PaymentRow[]
  totals?: PaymentTotalsData
  onCreateNew?: () => void
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 p-4">
        {payments.map((p) => (
          <CompactPaymentRowCard key={p.id} row={p} />
        ))}
      </div>
      {totals && <TotalsRow totals={totals} className="bg-white" />}
      <div className="sticky bottom-0 z-10 flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 bg-gray-100 px-2.5">
        {onCancel && (
          <Button
            variant="outline"
            size="xs"
            pressEffect
            className="px-2.5"
            onClick={onCancel}
          >
            {t('editor.payment.cancel')}
          </Button>
        )}
        <Button
          size="xs"
          pressEffect
          className="gap-2.5 px-3.75"
          onClick={onCreateNew}
        >
          <PlusIcon className="size-2.5" />
          {t('editor.payment.createNew')}
        </Button>
      </div>
    </div>
  )
}

function PaymentEditor({
  description,
  payments,
  totals,
  onCreate,
  onCreateNew,
  onCancel,
  className,
  compact,
}: PaymentEditorProps) {
  const { t } = useTranslation()
  const isEmpty = !payments || payments.length === 0
  const resolvedDescription = description ?? t('editor.payment.description')
  if (compact && payments && !isEmpty) {
    return (
      <CompactPaymentList
        payments={payments}
        totals={totals}
        onCreateNew={onCreateNew}
        onCancel={onCancel}
      />
    )
  }
  return (
    <div className={cn('flex max-h-125 w-full flex-col', className)}>
      {isEmpty ? (
        <PaymentEmpty description={resolvedDescription} onCreate={onCreate} />
      ) : (
        <PaymentList
          payments={payments}
          totals={totals}
          onCreateNew={onCreateNew}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

export { PaymentEditor }
export type {
  PaymentEditorProps,
  PaymentRow,
  PaymentBreakdown,
  PaymentTotalsData,
  PaymentVariant,
  PaymentStatusData,
  FlowBadgeData,
}
